/**
 * Admin Crates Items — manage cosmetics published from Crates Creator (crateOnly).
 */
import {
  deleteAdminShopCosmetic,
  fetchAdminShopCosmetics,
  patchAdminShopCosmetic,
  type AdminShopCosmeticRow,
  type CosmeticCategory,
} from "../lib/cloud";

const CATEGORIES: Array<{ id: CosmeticCategory; label: string }> = [
  { id: "hat", label: "Hat" },
  { id: "face", label: "Face" },
  { id: "cursor", label: "Body" },
  { id: "cursor_color", label: "Cursor" },
  { id: "trail", label: "Trails" },
  { id: "banner", label: "Banners" },
  { id: "theme", label: "Themes" },
];

export function mountAdminCrateItems(
  root: HTMLElement,
  opts?: {
    onEditItem?: (item: AdminShopCosmeticRow) => void;
    permissions?: { overwrite?: boolean; delete?: boolean };
  },
) {
  root.replaceChildren();

  const status = document.createElement("p");
  status.className = "accessHint";
  const wrap = document.createElement("div");
  wrap.className = "leaderboardWrap adminShopItemsWrap";
  root.appendChild(status);
  root.appendChild(wrap);

  let items: AdminShopCosmeticRow[] = [];

  async function load() {
    status.textContent = "Loading crate items…";
    try {
      const rows = await fetchAdminShopCosmetics();
      items = rows.filter((item) => !!item.crateOnly);
      render();
      status.textContent = `${items.length} crate item(s).`;
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Load failed.";
    }
  }

  function render() {
    wrap.replaceChildren();
    if (!items.length) {
      wrap.textContent = "No crate items yet. Publish one from Crates Creator.";
      return;
    }
    const table = document.createElement("table");
    table.className = "leaderboardTable adminShopItemsTable";
    table.innerHTML = `<thead><tr><th>Name</th><th>Category</th><th>Source</th><th>Options</th><th>Actions</th></tr></thead>`;
    const tbody = document.createElement("tbody");
    for (const item of items) {
      const tr = document.createElement("tr");

      const nameInput = document.createElement("textarea");
      nameInput.className = "formInput adminShopItemName";
      nameInput.rows = 2;
      nameInput.value = item.name;

      const categorySelect = document.createElement("select");
      categorySelect.className = "formInput";
      CATEGORIES.forEach(({ id, label }) => {
        const o = document.createElement("option");
        o.value = id;
        o.textContent = label;
        categorySelect.appendChild(o);
      });
      categorySelect.value = item.category || "cursor";

      const sourceNote = document.createElement("span");
      sourceNote.className = "accessHint";
      sourceNote.textContent = "Crate drop only";

      const customColorCheck = document.createElement("input");
      customColorCheck.type = "checkbox";
      customColorCheck.checked = !!item.allowCustomColor;
      const customColorLab = document.createElement("label");
      customColorLab.className = "adminShopCheckLabel";
      customColorLab.appendChild(customColorCheck);
      customColorLab.appendChild(document.createTextNode(" Custom color"));

      const repurchaseCheck = document.createElement("input");
      repurchaseCheck.type = "checkbox";
      repurchaseCheck.checked = !!item.allowRepurchase;
      const repurchaseLab = document.createElement("label");
      repurchaseLab.className = "adminShopCheckLabel";
      repurchaseLab.appendChild(repurchaseCheck);
      repurchaseLab.appendChild(document.createTextNode(" Re-tint"));

      const matchCursorCheck = document.createElement("input");
      matchCursorCheck.type = "checkbox";
      matchCursorCheck.checked = !!item.matchCursorColor;
      const matchCursorLab = document.createElement("label");
      matchCursorLab.className = "adminShopCheckLabel";
      matchCursorLab.appendChild(matchCursorCheck);
      matchCursorLab.appendChild(document.createTextNode(" Match cursor color"));

      const shadeInput = document.createElement("input");
      shadeInput.type = "range";
      shadeInput.min = "-100";
      shadeInput.max = "100";
      shadeInput.step = "1";
      shadeInput.className = "adminShopShadeSlider";
      const shadeInit =
        typeof item.cursorColorShade === "number" && Number.isFinite(item.cursorColorShade)
          ? Math.round(Math.max(-1, Math.min(1, item.cursorColorShade)) * 100)
          : 0;
      shadeInput.value = String(shadeInit);
      shadeInput.classList.toggle("hidden", !matchCursorCheck.checked);
      matchCursorCheck.onchange = () => {
        shadeInput.classList.toggle("hidden", !matchCursorCheck.checked);
      };

      const optsWrap = document.createElement("div");
      optsWrap.className = "adminShopItemOpts";
      optsWrap.appendChild(customColorLab);
      optsWrap.appendChild(repurchaseLab);
      optsWrap.appendChild(matchCursorLab);
      optsWrap.appendChild(shadeInput);

      const saveBtn = document.createElement("button");
      saveBtn.type = "button";
      saveBtn.className = "btn green";
      saveBtn.textContent = "Save";
      saveBtn.onclick = async () => {
        status.textContent = "Saving…";
        try {
          await patchAdminShopCosmetic({
            id: item.id,
            name: nameInput.value.trim(),
            category: categorySelect.value as CosmeticCategory,
            allowCustomColor: customColorCheck.checked,
            allowRepurchase: repurchaseCheck.checked,
            matchCursorColor: matchCursorCheck.checked,
            cursorColorShade: matchCursorCheck.checked
              ? Math.max(-1, Math.min(1, (Number(shadeInput.value) || 0) / 100))
              : 0,
          });
          status.textContent = "Saved.";
          await load();
        } catch (e) {
          status.textContent = e instanceof Error ? e.message : "Save failed.";
        }
      };

      const editBtn = document.createElement("button");
      editBtn.type = "button";
      editBtn.className = "btn";
      editBtn.textContent = "Edit cosmetic";
      editBtn.onclick = () => {
        if (!opts?.onEditItem) {
          status.textContent = "Crates Creator is not available.";
          return;
        }
        opts.onEditItem({
          ...item,
          name: nameInput.value.trim() || item.name,
          category: categorySelect.value as CosmeticCategory,
          allowCustomColor: customColorCheck.checked,
          allowRepurchase: repurchaseCheck.checked,
          matchCursorColor: matchCursorCheck.checked,
          cursorColorShade: matchCursorCheck.checked
            ? Math.max(-1, Math.min(1, (Number(shadeInput.value) || 0) / 100))
            : 0,
        });
      };

      const delBtn = document.createElement("button");
      delBtn.type = "button";
      delBtn.className = "btn";
      delBtn.textContent = "Delete";
      if (opts?.permissions?.delete === false) {
        delBtn.disabled = true;
        delBtn.title = "Missing Crates permission";
      }
      delBtn.onclick = async () => {
        if (opts?.permissions?.delete === false) {
          status.textContent = "Missing Crates permission.";
          return;
        }
        if (!confirm(`Delete crate item "${item.name}"?`)) return;
        status.textContent = "Deleting…";
        try {
          await deleteAdminShopCosmetic(item.id);
          status.textContent = "Deleted.";
          await load();
        } catch (e) {
          status.textContent = e instanceof Error ? e.message : "Delete failed.";
        }
      };

      const tdName = document.createElement("td");
      tdName.appendChild(nameInput);
      const tdCat = document.createElement("td");
      tdCat.appendChild(categorySelect);
      const tdSource = document.createElement("td");
      tdSource.appendChild(sourceNote);
      const tdOpts = document.createElement("td");
      tdOpts.appendChild(optsWrap);
      const tdAct = document.createElement("td");
      tdAct.appendChild(editBtn);
      tdAct.appendChild(saveBtn);
      tdAct.appendChild(delBtn);
      tr.appendChild(tdName);
      tr.appendChild(tdCat);
      tr.appendChild(tdSource);
      tr.appendChild(tdOpts);
      tr.appendChild(tdAct);
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    wrap.appendChild(table);
  }

  return {
    refresh: () => load(),
  };
}
